import type { ReactNode } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router";
import { STAFF_ROLE } from "../api/staffTypes";
import type { MeResponse, PartyType } from "../api/types";
import { Banner, Button, SkeletonBlock } from "../components/ui";
import { formatLocal } from "../lib/datetime";
import { useAuth, useSession } from "./AuthProvider";
import { homeFor } from "./pages";

export function SessionLoading() {
  return (
    <main className="auth-page">
      <div className="auth-card">
        <SkeletonBlock label="Checking your session" lines={4} />
      </div>
    </main>
  );
}

/** GET /v1/me: a staff account the console serves: the platform party with platform_reviewer (account.go:73-85). */
export function isStaffReviewer(me: MeResponse): boolean {
  return me.party_type === "platform" && me.roles.includes(STAFF_ROLE);
}

/**
 * Whether THIS session's own roles include platform_reviewer, regardless of
 * party_type. Migration 0070 (`cmd/ops grant-reviewer`) can grant the role
 * to an existing advertiser/publisher account, inserting a user_roles row
 * on both the account's own party and the platform party; GET /v1/me's
 * `roles` reflects the session's own-party row (internal/auth/store.go
 * UserRoles, scoped to sess.PartyID), so this is true for such a
 * dual-role account even though `party_type` stays "advertiser"/"publisher".
 * The backend's authority check (RequireRole + the role-matrix) doesn't
 * care about party_type either — see the comment on RequireStaff below.
 */
export function hasStaffRole(me: MeResponse): boolean {
  return me.roles.includes(STAFF_ROLE);
}

/**
 * A signed-in platform-party account WITHOUT the platform_reviewer role
 * (GET /v1/me party_type "platform", roles lacking it). Every internal
 * route would answer 403 insufficient_role (auth.RequireRole), so there
 * is no console to show; it says so instead.
 */
export function PlatformAccountNotice() {
  const { logout } = useAuth();
  return (
    <main className="auth-page">
      <div className="auth-card stack">
        <h1>Staff account</h1>
        <Banner tone="info" title="No reviewer role on this account">
          You're signed in with a VADS staff account that doesn't hold the platform reviewer role, so the staff console has
          nothing it can show you. The advertiser and publisher portals aren't for staff accounts.
        </Banner>
        <div>
          <Button variant="secondary" onClick={() => void logout()}>
            Sign out
          </Button>
        </div>
      </div>
    </main>
  );
}

/** Route guard: signed-in session of the given party type. */
export function RequireParty({ partyType }: { partyType: PartyType }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === "loading") return <SessionLoading />;
  if (state.status === "signed_out") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (state.status === "platform") return <Navigate to={STAFF_HOME} replace />;
  if (state.partyType !== partyType) return <Navigate to={homeFor(state.partyType)} replace />;
  return <Outlet />;
}

export const STAFF_HOME = "/staff";

/**
 * Route guard for the staff console: any session holding platform_reviewer
 * on its own party (GET /v1/me `roles`), whether that's a pure platform
 * party or an advertiser/publisher account also granted the role by
 * `cmd/ops grant-reviewer` (migration 0070). Anyone else never renders a
 * staff screen: an ordinary advertiser or publisher (no role) is sent to
 * their own portal, a signed-out visitor to sign-in, a platform account
 * without the role gets the notice (that account has no portal to fall
 * back to). This is defense in depth only: every /v1/internal/* route is
 * RequireSession + RequireRole("platform_reviewer") on the server, and the
 * SQL re-checks the role for the money commands.
 */
export function RequireStaff() {
  const { state } = useAuth();  const location = useLocation();
  if (state.status === "loading") return <SessionLoading />;
  if (state.status === "signed_out") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (state.status === "signed_in") {
    if (hasStaffRole(state.me)) return <Outlet />;
    return <Navigate to={homeFor(state.partyType)} replace />;
  }
  if (!isStaffReviewer(state.me)) return <PlatformAccountNotice />;
  return <Outlet />;
}

/** For screens rendered only inside the staff route tree (RequireStaff). */
export function useStaffSession(): { me: MeResponse } {
  const { state } = useAuth();
  if (state.status === "platform") return { me: state.me };
  if (state.status === "signed_in" && hasStaffRole(state.me)) return { me: state.me };
  throw new Error("useStaffSession used outside the staff route tree");
}

/**
 * UX-DESIGN.md §2.1 step 3: "the portal blocks wallet access entirely until
 * MFA is set up, not just prompts for it" (and §3.0 for payouts). Whether a
 * factor is enrolled comes from GET /v1/me `mfa.enrolled` (G-01). The server
 * still enforces step-up on the financial calls themselves; this gate is
 * the up-front UX, and the hint under it says whether a step-up will be
 * asked for (`mfa_fresh`, `mfa_fresh_until`).
 */
export function RequireMfa({ children }: { children: ReactNode }) {
  const session = useSession();
  if (session.mfa === "not_enrolled") {
    return (
      <Banner tone="warning" title="Two-step verification is required here">
        <div className="stack-sm">
          <span>
            This screen moves money or controls where it goes. Set up a security key or authenticator app first — it takes
            about a minute.
          </span>
          <span>
            <Link className="btn btn-primary btn-sm" to={`${homeFor(session.partyType)}/security?setup=1`}>
              Set up two-step verification
            </Link>
          </span>
        </div>
      </Banner>
    );
  }
  return (
    <>
      <StepUpHint />
      {children}
    </>
  );
}

/** Whether money actions on this screen will ask the user to confirm it's them (RequireRecentMFA, 15 minutes). */
export function StepUpHint() {
  const { me } = useSession();
  const until = me.mfa_fresh && me.mfa_fresh_until ? new Date(me.mfa_fresh_until) : null;
  return (
    <p className="xs muted" data-testid="step-up-hint">
      {until && until.getTime() > Date.now()
        ? `You confirmed it's you recently: money actions won't ask again until ${formatLocal(until)}.`
        : "Money actions here ask you to confirm it's you first (security key or authenticator code), valid for 15 minutes."}
    </p>
  );
}
